import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";

import { ApiError, api } from "./lib/api";
import { PLAN_LABEL } from "./lib/types";
import type { AuthMe, HealthCheck, Settings, User } from "./lib/types";

// ---------------------------------------------------------------- toasts

type Toast = { id: number; message: string; tone: "info" | "error" | "good" };

const ToastContext = createContext<{
  push: (message: string, tone?: Toast["tone"]) => void;
  pushError: (error: unknown) => void;
}>({ push: () => {}, pushError: () => {} });

export const useToast = () => useContext(ToastContext);

/** Turn any thrown value into the clearest sentence we can offer. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.hint ? `${error.message} ${error.hint}` : error.message;
  }
  if (error instanceof Error) return error.message;
  return "Something went wrong.";
}

// -------------------------------------------------------------- settings

const SettingsContext = createContext<{ settings: Settings | null; health: HealthCheck | null }>({
  settings: null,
  health: null,
});

export const useSettings = () => useContext(SettingsContext);

// ------------------------------------------------------------------ auth

interface AuthValue {
  user: User | null;
  auth: AuthMe | null;
  loading: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthValue>({
  user: null,
  auth: null,
  loading: true,
  refresh: async () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

// ------------------------------------------------------------------ app

export default function App() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [health, setHealth] = useState<HealthCheck | null>(null);
  const [auth, setAuth] = useState<AuthMe | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [theme, setTheme] = useState<"light" | "dark" | "system">(
    () => (localStorage.getItem("makeitanki-theme") as "light" | "dark" | "system") ?? "system",
  );
  const navigate = useNavigate();

  const push = useCallback((message: string, tone: Toast["tone"] = "info") => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { id, message, tone }]);
    window.setTimeout(() => setToasts((c) => c.filter((t) => t.id !== id)), 6000);
  }, []);

  const pushError = useCallback((error: unknown) => push(errorMessage(error), "error"), [push]);

  const refreshAuth = useCallback(async () => {
    try {
      setAuth(await api.me());
    } catch {
      setAuth(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  // Settings/health describe server-side AI configuration - nothing a
  // signed-out visitor on the landing page needs, and the endpoints require a
  // session now anyway.
  useEffect(() => {
    if (!auth) {
      setSettings(null);
      setHealth(null);
      return;
    }
    api.settings().then(setSettings).catch(() => {});
    api.health().then(setHealth).catch(() => {});
  }, [auth]);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "system") root.removeAttribute("data-theme");
    else root.setAttribute("data-theme", theme);
    localStorage.setItem("makeitanki-theme", theme);
  }, [theme]);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* the cookie may already be gone; sign out locally regardless */
    }
    setAuth(null);
    navigate("/");
  }, [navigate]);

  const toastValue = useMemo(() => ({ push, pushError }), [push, pushError]);
  const settingsValue = useMemo(() => ({ settings, health }), [settings, health]);
  const authValue = useMemo<AuthValue>(
    () => ({ user: auth?.user ?? null, auth, loading: authLoading, refresh: refreshAuth, signOut }),
    [auth, authLoading, refreshAuth, signOut],
  );

  const cycleTheme = () =>
    setTheme((t) => (t === "system" ? "light" : t === "light" ? "dark" : "system"));

  return (
    <AuthContext.Provider value={authValue}>
      <SettingsContext.Provider value={settingsValue}>
        <ToastContext.Provider value={toastValue}>
          <div className="app">
            <header className="topbar">
              <Link to="/" className="brand">
                <span className="brand-mark">M</span>
                MakeitAnki
              </Link>
              <div className="topbar-spacer" />

              {settings && !settings.llm_key_present && (
                <span className="chip chip-warn" title="The AI provider is not configured on this server">
                  AI service unavailable
                </span>
              )}
              {settings && (
                <span className="chip chip-outline small" title="Model used for generation">
                  {settings.llm_model}
                </span>
              )}

              {!authLoading && auth && (
                <>
                  <Link to="/pricing" className="chip chip-accent" title="Your plan">
                    {PLAN_LABEL[auth.plan]}
                  </Link>
                  <span className="small faint">{auth.user.email}</span>
                  <button className="btn-ghost btn-sm" onClick={signOut}>
                    Sign out
                  </button>
                </>
              )}
              {!authLoading && !auth && (
                <Link to="/" className="btn btn-sm btn-primary">
                  Sign in
                </Link>
              )}

              <button
                className="btn-ghost btn-sm"
                onClick={cycleTheme}
                title={`Theme: ${theme}. Click to change.`}
                aria-label="Change theme"
              >
                {theme === "system" ? "◐" : theme === "light" ? "☀" : "☾"}
              </button>
            </header>

            <Outlet />

            <div className="toast-stack">
              {toasts.map((toast) => (
                <div
                  key={toast.id}
                  className={`toast ${toast.tone === "error" ? "is-error" : toast.tone === "good" ? "is-good" : ""}`}
                  role="status"
                >
                  {toast.message}
                </div>
              ))}
            </div>
          </div>
        </ToastContext.Provider>
      </SettingsContext.Provider>
    </AuthContext.Provider>
  );
}
